import { BimTab } from '../tab';
import { BimTree } from '../tree';
import type { TreeNodeConfig } from '../tree/types';
import { TreeNodeCheckState } from '../tree/types';
import type { BimTreeNode } from '../tree/tree-node';
import type { ManagerRegistry } from '../../core/manager-registry';
import { localeManager } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { ThemeConfig } from '../../themes/types';
import type { ComponentTreeDrawerTabComponent } from './types';

interface EngineTreeNode {
    name?: string;
    id?: string | null;
    ids?: string[] | null;
    children?: EngineTreeNode[] | null;
    isLeaf?: boolean;
}

interface EngineModelData {
    name?: string;
    url: string;
    children?: EngineTreeNode[] | null;
}

interface TransformedNodeData extends EngineTreeNode {
    _modelUrl: string;
}

interface ModelParam {
    url: string;
    ids: number[];
}

function fnv1a32(input: string, seed: number): number {
    let h = seed >>> 0;
    for (let i = 0; i < input.length; i++) {
        const c = input.charCodeAt(i);
        h ^= c & 0xff;
        h = Math.imul(h, 0x01000193);
        h ^= (c >>> 8) & 0xff;
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

async function hashIds(ids: string[]): Promise<string> {
    const str = JSON.stringify(ids);
    const h1 = fnv1a32(str, 0x811c9dc5);
    const h2 = fnv1a32(str, 0x811c9dc5 ^ 0x9e3779b9);
    return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function collectModelParams(node: BimTreeNode): ModelParam[] {
    const grouped = new Map<string, Set<number>>();

    const collect = (current: BimTreeNode): void => {
        const data = current.config.data as TransformedNodeData | undefined;
        const modelUrl = data?._modelUrl;
        if (modelUrl && data?.ids?.length) {
            let idSet = grouped.get(modelUrl);
            if (!idSet) {
                idSet = new Set<number>();
                grouped.set(modelUrl, idSet);
            }
            for (const rawId of data.ids) {
                const id = Number(rawId);
                if (Number.isFinite(id)) {
                    idSet.add(id);
                }
            }
        }

        for (const child of current.children || []) {
            collect(child);
        }
    };

    collect(node);

    const result: ModelParam[] = [];
    for (const [url, idSet] of grouped) {
        if (idSet.size > 0) {
            result.push({ url, ids: Array.from(idSet) });
        }
    }
    return result;
}

let nodeIdCounter = 0;

async function transformTreeData(apiData: EngineModelData[]): Promise<TreeNodeConfig[]> {
    if (!apiData || apiData.length === 0) return [];

    const transformNode = async (node: EngineTreeNode, modelUrl: string): Promise<TreeNodeConfig> => {
        const hasChildren = node.children && node.children.length > 0;

        let id: string;
        if (node.ids?.length) {
            id = await hashIds(node.ids);
        } else if (node.id) {
            id = node.id;
        } else {
            id = `node_${++nodeIdCounter}`;
        }

        return {
            id,
            label: node.name || '未命名',
            expanded: false,
            checked: true,
            children: hasChildren
                ? await Promise.all(node.children!.map(child => transformNode(child, modelUrl)))
                : undefined,
            data: {
                ...node,
                _modelUrl: modelUrl
            } as TransformedNodeData
        };
    };

    return Promise.all(apiData.map(async (model) => {
        const hasChildren = model.children && model.children.length > 0;
        const modelUrl = model.url;

        return {
            id: modelUrl,
            label: model.name || '模型',
            expanded: true,
            checked: true,
            children: hasChildren
                ? await Promise.all(model.children!.map(child => transformNode(child, modelUrl)))
                : undefined,
            data: {
                _modelUrl: modelUrl
            } as TransformedNodeData
        };
    }));
}

export class ComponentTreeDrawerConstructTreeTab implements ComponentTreeDrawerTabComponent {
    public readonly element: HTMLElement;

    private readonly registry: ManagerRegistry | null;
    private readonly mountElement: HTMLElement;
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private unsubscribeShowAll: (() => void) | null = null;
    private activeTab: BimTab | null = null;
    private levelTree: BimTree | null = null;
    private typeTree: BimTree | null = null;
    private majorTree: BimTree | null = null;
    private refreshToken = 0;
    private isRendering = false;
    private pendingRefresh = false;

    constructor(registry: ManagerRegistry | null) {
        this.registry = registry;
        this.element = document.createElement('section');
        this.element.className = 'component-tree-drawer-construct-tree';
        this.element.dataset.tab = 'construct-tree';

        this.mountElement = document.createElement('div');
        this.mountElement.className = 'component-tree-drawer-construct-tree-mount';
        this.element.appendChild(this.mountElement);
    }

    public init(): void {
        this.setTheme(themeManager.getTheme());
        this.unsubscribeLocale = localeManager.subscribe(() => {
            this.setLocales();
            void this.refresh();
        });
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));
    }

    public getElement(): HTMLElement {
        return this.element;
    }

    public onShow(): void {
        void this.refresh();
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-component-tree-page-title', theme.textPrimary);
        style.setProperty('--bim-component-tree-page-description', theme.textSecondary);
        style.setProperty('--bim-component-tree-content-bg', theme.bgElevated);
    }

    public setLocales(): void {
        // BimTab and BimTree handle their own locale subscriptions.
    }

    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.unsubscribeShowAll) {
            this.unsubscribeShowAll();
            this.unsubscribeShowAll = null;
        }
        this.destroyCurrentContent();
        this.element.remove();
    }

    private async refresh(): Promise<void> {
        if (!this.registry || !this.registry.engine3d) {
            return;
        }

        if (this.isRendering) {
            this.pendingRefresh = true;
            return;
        }

        this.isRendering = true;
        const currentToken = ++this.refreshToken;

        try {
            const engineComponent = this.registry.engine3d.getEngineComponent?.();
            const levelTreeData = engineComponent?.getLevelTreeData() ?? [];
            const typeTreeData = engineComponent?.getTypeTreeData() ?? [];
            const majorTreeData = engineComponent?.getMajorTreeData() ?? [];

            const [levelTree, typeTree, majorTree] = await Promise.all([
                this.createTree(levelTreeData),
                this.createTree(typeTreeData),
                this.createTree(majorTreeData)
            ]);

            if (currentToken !== this.refreshToken) {
                levelTree.destroy();
                typeTree.destroy();
                majorTree.destroy();
                return;
            }

            this.destroyCurrentContent();

            this.levelTree = levelTree;
            this.typeTree = typeTree;
            this.majorTree = majorTree;

            const componentPanel = document.createElement('div');
            componentPanel.className = 'component-tree-drawer-construct-tree-panel';
            componentPanel.appendChild(levelTree.element);

            const typePanel = document.createElement('div');
            typePanel.className = 'component-tree-drawer-construct-tree-panel';
            typePanel.appendChild(typeTree.element);

            const majorPanel = document.createElement('div');
            majorPanel.className = 'component-tree-drawer-construct-tree-panel';
            majorPanel.appendChild(majorTree.element);

            const tabMount = document.createElement('div');
            tabMount.className = 'component-tree-drawer-construct-tree-tab-container';

            const tab = new BimTab({
                container: tabMount,
                tabs: [
                    { id: 'component', title: 'tab.component', content: componentPanel },
                    { id: 'type', title: 'tab.type', content: typePanel },
                    { id: 'major', title: 'tab.major', content: majorPanel }
                ],
                activeId: 'component'
            });
            tab.init();
            this.activeTab = tab;
            this.mountElement.appendChild(tabMount);

            this.resetAllTrees();
            this.unsubscribeShowAll = this.registry.on('menu:show-all', () => {
                this.resetAllTrees();
            });
        } finally {
            this.isRendering = false;
            if (this.pendingRefresh) {
                this.pendingRefresh = false;
                void this.refresh();
            }
        }
    }

    private async createTree(data: EngineModelData[]): Promise<BimTree> {
        const transformedData = await transformTreeData(data);
        const tree = new BimTree({
            data: transformedData,
            checkable: true,
            indent: 0,
            enableSearch: true,
            checkStrictly: true,
            defaultExpandAll: true,
            onNodeCheck: (node) => {
                const modelParam = collectModelParams(node);
                if (!modelParam.length) return;

                if (node.checkState === TreeNodeCheckState.Checked) {
                    this.registry?.engine3d?.getEngineComponent?.()?.showModel(modelParam);
                } else {
                    this.registry?.engine3d?.getEngineComponent?.()?.hideModels(modelParam);
                }
            },
            onNodeSelect: (node) => {
                const modelParam = collectModelParams(node);
                if (!modelParam.length) return;

                const engineComponent = this.registry?.engine3d?.getEngineComponent?.();
                engineComponent?.unhighlightAllModels();
                engineComponent?.highlightModel(modelParam);
                engineComponent?.viewScaleToModel(modelParam);
            },
            onNodeDeselect: () => {
                this.registry?.engine3d?.getEngineComponent?.()?.unhighlightAllModels();
            }
        });
        tree.init();
        return tree;
    }

    private resetAllTrees(): void {
        const engineComponent = this.registry?.engine3d?.getEngineComponent?.();
        engineComponent?.showAllModels();
        this.levelTree?.checkAllNodes(true);
        this.typeTree?.checkAllNodes(true);
        this.majorTree?.checkAllNodes(true);
    }

    private destroyCurrentContent(): void {
        if (this.unsubscribeShowAll) {
            this.unsubscribeShowAll();
            this.unsubscribeShowAll = null;
        }
        this.activeTab?.destroy();
        this.activeTab = null;
        this.levelTree?.destroy();
        this.levelTree = null;
        this.typeTree?.destroy();
        this.typeTree = null;
        this.majorTree?.destroy();
        this.majorTree = null;
        this.mountElement.innerHTML = '';
    }
}
